/**
 * storico-personalizzati-service.ts
 * 
 * Service per accedere ai dati storici dal portale Personalizzati
 * Accede alle tabelle cst.StoricoProgetti, cst.Storico2019Progetti e relativi allegati
 */

import { apiClient } from './api';

export interface StoricoProgettoPersonalizzati {
  id: string;
  titoloProgetto: string;
  idProgressivo: string;
  tipologiaProgetto: string;
  path: string;
  num_Decoro_CSPD: string;
  statoFlusso: string;
  richiesta: string;
  richiesta_Descrizione: string;
  richiesta_NumGG?: number;
  priorita_Pj: string;
  topClient: string;
  richiestaCosting: string;
  revisionato?: boolean;
  noteRichiestaModifica: string;
  ingaggioBU: string;
  ingaggioTipoBU: string;
  ingaggioTipoBUquorum: string;
  odV: string;
  topClientNote: string;
  areaCommerciale: string;
  areaManager: string;
  assortimentoSL: string;
  codiceCliente: string;
  dataInvioRichiesta?: string;
  dataEffettiva?: string;
  dataStimata?: string;
  dataConsegna?: string;
  dataRichiestaConsegna?: string;
  dataRicezioneMaterialeSupporto?: string;
  fatturatoStimato?: number;
  numeroArticoli?: number;
  previsioneVenditaPZ?: number;
  prioritaDirezione: string;
  createdBy: string;
  numGGProdRich?: number;
  inAttesa: string;
  numGGProdSTD?: number;
  created?: string;
  dataEffettivaGantt?: string;
  wfApprovazione: string;
  richiesta_TipologiaRichiesta: string;
  odVCliente: string;
  modified?: string;
  modifiedBy: string;
  wf_TST: string;
  taskStatus: string;
  clienteProgettoInfo: string;
  storicoInfoCliente: string;
  itemType: string;
  attachments: StoricoAllegatoPersonalizzati[];
}

export interface StoricoAllegatoPersonalizzati {
  id: string;
  progettoId: string;
  fileName: string;
  blobName: string;
  url: string;
  importedDate?: string;
}

class StoricoPersonalizzatiService {
  // Storico Progetti (cst.StoricoProgetti)
  async getStoricoProgetti(): Promise<StoricoProgettoPersonalizzati[]> {
    const response = await apiClient.get<StoricoProgettoPersonalizzati[]>('/api/StoricoProgetti');
    return response as any; // Response is already the array
  }

  async getStoricoProgettoById(id: string): Promise<StoricoProgettoPersonalizzati> {
    const response = await apiClient.get<StoricoProgettoPersonalizzati>(`/api/StoricoProgetti/${id}`);
    return response as any;
  }

  async getStoricoProgettoAllegati(id: string): Promise<StoricoAllegatoPersonalizzati[]> {
    const response = await apiClient.get<StoricoAllegatoPersonalizzati[]>(`/api/StoricoProgetti/${id}/allegati`);
    return response as any;
  }

  // Storico 2019 Progetti (cst.Storico2019Progetti)
  async getStorico2019Progetti(): Promise<StoricoProgettoPersonalizzati[]> {
    const response = await apiClient.get<StoricoProgettoPersonalizzati[]>('/api/Storico2019Progetti');
    return response as any;
  }

  async getStorico2019ProgettoById(id: string): Promise<StoricoProgettoPersonalizzati> {
    const response = await apiClient.get<StoricoProgettoPersonalizzati>(`/api/Storico2019Progetti/${id}`);
    return response as any;
  }

  async getStorico2019ProgettoAllegati(id: string): Promise<StoricoAllegatoPersonalizzati[]> {
    const response = await apiClient.get<StoricoAllegatoPersonalizzati[]>(`/api/Storico2019Progetti/${id}/allegati`);
    return response as any;
  }

  // Genera URL con SAS token per il download
  async getStoricoProgettoAllegatoDownloadUrl(allegatoId: string): Promise<{ url: string; fileName: string }> {
    const response = await apiClient.get<{ url: string; fileName: string }>(`/api/StoricoProgetti/allegati/${allegatoId}/download-url`);
    return response as any;
  }

  async getStorico2019ProgettoAllegatoDownloadUrl(allegatoId: string): Promise<{ url: string; fileName: string }> {
    const response = await apiClient.get<{ url: string; fileName: string }>(`/api/Storico2019Progetti/allegati/${allegatoId}/download-url`);
    return response as any;
  }

  // Metodo unificato per ottenere tutti gli storici
  async getAllStorici(): Promise<{
    storicoProgetti: StoricoProgettoPersonalizzati[];
    storico2019Progetti: StoricoProgettoPersonalizzati[];
  }> {
    const [storicoProgetti, storico2019Progetti] = await Promise.all([
      this.getStoricoProgetti(),
      this.getStorico2019Progetti(),
    ]);

    return {
      storicoProgetti,
      storico2019Progetti,
    };
  }
}

export const storicoPersonalizzatiService = new StoricoPersonalizzatiService();
export default storicoPersonalizzatiService;
